import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

const navLinks = [
  { name: "Home", hash: "home" },
  { name: "About", hash: "about" },
  { name: "Products", hash: "products" },
  { name: "Services", hash: "services" },
  { name: "Why Us", hash: "why-us" },
  { name: "FAQ", hash: "faq" },
  { name: "Contact", hash: "contact" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const next = window.scrollY > 20;
        if (next !== lastScrolled) {
          lastScrolled = next;
          setIsScrolled(next);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hrefFor = (hash: string) => (isHome ? `#${hash}` : `/#${hash}`);

  const handleNavClick = (e: React.MouseEvent, hash: string) => {
    if (isHome) {
      e.preventDefault();
      const target = document.getElementById(hash);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // On non-home routes, allow default Link navigation to "/#hash"
    // Index.tsx handles scrolling to the hash on mount.
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-sm border-b border-border py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo — always returns to homepage */}
        <Link
          to="/"
          className="flex items-center group"
          aria-label="SAILKHA TRADE — Home"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              const home = document.getElementById("home");
              if (home) {
                home.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }
          }}
        >
          <span className="font-logo text-xl md:text-2xl font-semibold tracking-wide text-foreground transition-all duration-300 group-hover:tracking-widest group-hover:text-primary">
            SAILKHA TRADE
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={hrefFor(link.hash)}
              onClick={(e) => handleNavClick(e, link.hash)}
              className="text-sm tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors duration-200 hover-underline"
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:block">
          <Button
            className="bg-foreground text-background hover:bg-foreground/90 rounded-none h-10 px-6 text-xs tracking-wide-elegant uppercase"
            asChild
          >
            <Link
              to={hrefFor("contact")}
              onClick={(e) => {
                analytics.quoteClick("header_desktop");
                handleNavClick(e, "contact");
              }}
            >
              Get a Quote
            </Link>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-t border-border"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={hrefFor(link.hash)}
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      if (isHome) {
                        e.preventDefault();
                        const target = document.getElementById(link.hash);
                        if (target) {
                          setTimeout(() => {
                            target.scrollIntoView({ behavior: "smooth" });
                          }, 300);
                        }
                      }
                    }}
                    className="block text-foreground py-3 text-sm tracking-wide uppercase border-b border-border last:border-0"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <Button
                className="mt-4 bg-foreground text-background hover:bg-foreground/90 rounded-none h-12 text-xs tracking-wide-elegant uppercase"
                onClick={() => {
                  analytics.quoteClick("header_mobile");
                  setIsMobileMenuOpen(false);
                  if (isHome) {
                    const target = document.getElementById("contact");
                    if (target) {
                      setTimeout(() => {
                        target.scrollIntoView({ behavior: "smooth" });
                      }, 300);
                    }
                  } else {
                    navigate("/#contact");
                  }
                }}
              >
                Get a Quote
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
